import React, {CSSProperties, useState} from 'react'

import ControlLabel from './ControlLabel.js'
import {FieldProps} from './types.js'
import {choiceTokens, getChoiceBoxColors, getChoiceOverlayStyle} from './choiceStyles.js'
import {markFieldRowControl, useFieldRowOffset} from './FormRowContext.js'

export type RadioButtonValue = string | number | boolean

// Called with {target: {value}} on click and when pressing space
export type RadioButtonChangeEvent = {target: {value: RadioButtonValue}}

export interface RadioButtonProps extends FieldProps {
    value?: RadioButtonValue
    checked?: boolean
    onChange?: (event: RadioButtonChangeEvent) => void
    labelStyle?: CSSProperties
    clickableLabel?: boolean
    hideDot?: boolean
    unsetWhiteSpace?: boolean
}

const RadioButton = ({disabled, readOnly, checked, value, onChange, label, info, style = {}, labelStyle = {}, clickableLabel, hideDot, unsetWhiteSpace}: RadioButtonProps) => {
    const [focused, setFocused] = useState(false)
    const [hover, setHover] = useState<boolean>()
    const rowOffset = useFieldRowOffset(choiceTokens.height)

    // Read only keeps the normal look but ignores clicks and keys, like disabled
    const interactive = !disabled && !readOnly

    const onClick = () => {
        if (interactive) {
            onChange?.({target: {value: value}})
        }
    }

    // Space behaves like a click; preventDefault stops the page from scrolling
    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === ' ' || event.key === 'Spacebar' || event.keyCode === 32) {
            event.preventDefault()
            onClick()
        }
    }

    // Without the dot the label is the only clickable part, so it takes the focus instead.
    // Only keys pressed on the label itself count, not ones bubbling up from inputs or buttons inside it
    const onKeyDownLabel = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            onKeyDown(event)
        }
    }

    const labelFocusable = hideDot && clickableLabel

    const overLayStyle = getChoiceOverlayStyle({highlighted: hover || focused, interactive})

    // The ring keeps the unchecked background and takes the checked colour as border; the dot is the checked colour
    const ringColors = getChoiceBoxColors({checked: false, disabled})
    const checkedColors = getChoiceBoxColors({checked: true, disabled})

    const checkboxStyle: CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: ringColors.background,
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `1px solid ${checked ? checkedColors.border : ringColors.border}`,
        outline: 'none'
    }

    const dotStyle: CSSProperties = {
        background: checked ? checkedColors.background : 'transparent',
        width: checked ? 10 : 0,
        height: checked ? 10 : 0,
        borderRadius: '50%',
        transition: 'all .1s ease'
    }

    const defaultLabelStyle: CSSProperties = {
        marginLeft: choiceTokens.labelGap.radioButton.left,
        marginRight: choiceTokens.labelGap.radioButton.right,
        cursor: !interactive ? 'default' : clickableLabel ? 'pointer' : 'auto',
        ...labelStyle
    }

    const defaultStyle: CSSProperties = {display: 'flex', alignItems: 'center', height: choiceTokens.height, ...choiceTokens.margins.radioButton, ...rowOffset, ...style}

    return (
        <div style={defaultStyle}>
            {!hideDot &&
                <div
                    style={overLayStyle}
                    onClick={onClick}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                >
                    <div
                        tabIndex={disabled ? -1 : 0}
                        role='radio'
                        aria-checked={!!checked}
                        aria-disabled={disabled || undefined}
                        style={checkboxStyle}
                        onKeyDown={onKeyDown}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                    >
                        <div style={dotStyle} />
                    </div>
                </div>
            }

            {label &&
                <ControlLabel
                    style={defaultLabelStyle}
                    info={info}
                    disabled={disabled}
                    onClick={clickableLabel ? onClick : undefined}
                    tabIndex={labelFocusable ? (disabled ? -1 : 0) : undefined}
                    role={labelFocusable ? 'radio' : undefined}
                    aria-checked={labelFocusable ? !!checked : undefined}
                    aria-disabled={labelFocusable && disabled ? true : undefined}
                    onKeyDown={labelFocusable ? onKeyDownLabel : undefined}
                >
                    <div style={{whiteSpace: unsetWhiteSpace ? 'unset' : 'nowrap'}}>{label}</div>
                </ControlLabel>
            }
        </div>
    )
}

export default markFieldRowControl(RadioButton)
